import React, { useState, useEffect } from 'react';
import {
    Box,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatMonthDisplay } from '../../../../utils/base';
import CommonCard from '../../../../components/card/CommonCard';
import RankingCard from '../cards/rankingCard';
import RankingLineChart from '../../../../components/charts/lineChart/RankingLineChart';
import Toggle from '../../../../components/toggle';
import { rankAPI } from '../../../../actions/imsServer';

export const RankingTab = ({ userData, memberships = [], selectedOrganization = null }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(true);
    const [activeView, setActiveView] = useState(0); // 0: 월간경험치랭킹, 1: 누적경험치랭킹
    const [selectedMembership, setSelectedMembership] = useState(null);
    const [rankingHistoryData, setRankingHistoryData] = useState([]);

    // 선택된 기관에 해당하는 멤버십 선택
    useEffect(() => {
        if (memberships && memberships.length > 0) {
            if (selectedOrganization) {
                // selectedOrganization에 해당하는 멤버십 찾기
                const foundMembership = memberships.find(
                    m => String(m.organizationId) === String(selectedOrganization)
                );
                if (foundMembership) {
                    setSelectedMembership(foundMembership);
                } else if (!selectedMembership) {
                    // 찾지 못하면 첫 번째 멤버십 선택
                    setSelectedMembership(memberships[0]);
                }
            } else if (!selectedMembership) {
                // selectedOrganization이 없으면 첫 번째 멤버십 선택
                setSelectedMembership(memberships[0]);
            }
        }
    }, [memberships, selectedOrganization, selectedMembership]);

    // 월별 랭킹 히스토리 데이터 가져오기
    useEffect(() => {
        const fetchRankingHistory = async () => {
            if (!userData?.id || !selectedMembership) return;

            try {
                const today = new Date();
                const currentYear = today.getFullYear();
                const currentMonth = today.getMonth() + 1;

                // 현재 월부터 과거 12개월 데이터 가져오기
                const historyData = [];

                for (let i = 0; i < 12; i++) {
                    let year = currentYear;
                    let month = currentMonth - i; // 현재 월부터 과거로

                    if (month <= 0) {
                        month += 12;
                        year -= 1;
                    }

                    const monthStr = `${year}-${String(month).padStart(2, '0')}`;
                    const monthLabel = formatMonthDisplay(monthStr);

                    try {
                        let response;
                        if (activeView === 0) {
                            // 월간 랭킹 - 각 월별로 월간 랭킹 데이터 가져오기
                            response = await rankAPI.getMonthlyRanks({
                                month: monthStr,
                                userId: userData.id,
                                organizationId: selectedMembership?.organizationId,
                                division: selectedMembership?.division,
                                section: selectedMembership?.section
                            });
                        } else {
                            // 누적 랭킹 - 각 월 시점의 누적 랭킹 데이터 가져오기                            
                            response = await rankAPI.getRanks({
                                month: monthStr,
                                userId: userData.id,
                                organizationId: selectedMembership?.organizationId,
                                division: selectedMembership?.division,
                                section: selectedMembership?.section
                            });
                        }

                        const rank = response?.data?.ranks?.length > 0 ? response?.data?.ranks[0]?.rank : 0;

                        historyData.push({
                            month: monthLabel,
                            value: rank || 0
                        });
                    } catch (error) {
                        console.error(`랭킹 데이터 가져오기 실패 (${monthStr}):`, error);
                        historyData.push({
                            month: monthLabel,
                            value: 0
                        });
                    }
                }

                // 과거부터 현재 순서로 정렬 (역순)
                historyData.reverse();

                // 차트 데이터 구성
                const charts = [];

                // Section 차트
                if (selectedMembership?.section) {
                    const sectionData = historyData.map(d => ({ ...d }));
                    charts.push({
                        title: selectedMembership.section || t('class'),
                        data: sectionData,
                        color: theme.palette.figma.time
                    });
                }

                // Division 차트
                if (selectedMembership?.division) {
                    const divisionData = historyData.map(d => ({ ...d }));
                    charts.push({
                        title: selectedMembership.division || t('grade'),
                        data: divisionData,
                        color: theme.palette.figma.time
                    });
                }

                // Organization 차트
                if (selectedMembership?.organizationId) {
                    const orgData = historyData.map(d => ({ ...d }));
                    charts.push({
                        title: selectedMembership.organization?.name || t('whole_school'),
                        data: orgData,
                        color: theme.palette.figma.time
                    });
                }
                setRankingHistoryData(charts);
            } catch (error) {
                console.error('랭킹 히스토리 데이터 가져오기 실패:', error);
            }
        };

        if (activeView === 0 || activeView === 1) {
            fetchRankingHistory();
        }
    }, [userData?.id, selectedMembership, activeView, theme.palette.figma, t]);

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* 상단 뷰 선택기 */}
            <Toggle
                options={[t('monthly_experience_ranking'), t('cumulative_experience_ranking')]}
                value={activeView}
                onChange={(newView) => {
                    setActiveView(newView);
                }}
            />

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* 메인 콘텐츠 */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* 좌측 랭킹 카드 */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {selectedMembership && (
                            <RankingCard
                                userData={userData}
                                membership={selectedMembership}
                                toggleActive={activeView}
                                hideToggle={true}
                            />
                        )}
                    </Box>

                    {/* 우측 라인 차트 */}
                    <Box sx={{ flex: 1 }}>
                        <CommonCard>
                            <RankingLineChart
                                charts={rankingHistoryData}
                            />
                        </CommonCard>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};


export default RankingTab;
